import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Copy, Link2, Plus, RefreshCw, Send, Speaker, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  InviteCode,
  InviteCodeRequest,
  createInviteCode,
  declineInviteCodeRequest,
  formatInviteCode,
  markInviteCodeRequestHandled,
  revokeInviteCode,
  sendInviteCodeForRequest,
  subscribeToInviteCodeRequests,
  subscribeToInviteCodes,
} from '../../services/adminInvites';
import { signUpErrorMessage } from '../../services/betaSignUp';
import { DjAccount, listDjAccounts, setDjAccountPaused } from '../../services/accountStatus';
import { approveTierUpgrade, subscribeToGatedEvents, updateEventSizeGate } from '../../services/adminEvents';
import { computeEventStatus, Event } from '../../services/events';
import { GUEST_TIERS, tierLabel } from '../../utils/eventTiers';
import { setLinkEditingRevoked } from '../../services/linkReviews';
import { LinkLibrarySection } from './LinkLibrarySection';
import { DeletedEventsSection } from './DeletedEventsSection';
import { NextEventContactsSection } from './NextEventContactsSection';

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

function signUpLink(code: string): string {
  return `${window.location.origin}/register?code=${formatInviteCode(code)}`;
}

async function copyToClipboard(text: string, successMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch (error) {
    console.error('Copy to clipboard failed', error);
    toast.error('Could not copy. Select the text and copy it by hand.');
  }
}

const sectionClass = 'bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4';
const headingClass = 'text-lg font-semibold text-gray-900 dark:text-white';
const tableClass = 'w-full text-sm text-left text-gray-700 dark:text-gray-300';
const cellClass = 'py-2 pr-4 align-top';

function EmailProblem({ request }: { request: InviteCodeRequest }) {
  if (request.requesterEmailStatus === 'failed') {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Email failed: {request.requesterEmailError ?? 'unknown error'}
      </p>
    );
  }
  if (request.requesterEmailStatus === 'skipped') {
    return (
      <p className="text-sm text-amber-700 dark:text-amber-400">
        Email not sent: email sending isn't set up.
      </p>
    );
  }
  return null;
}

const MAX_PAUSE_NOTE_LENGTH = 500;

function djAccountName(account: DjAccount): string {
  return account.displayName || account.email || account.uid;
}

function DjAccountsSection() {
  const [accounts, setAccounts] = useState<DjAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [confirmingPauseUid, setConfirmingPauseUid] = useState<string | null>(null);
  const [pauseNote, setPauseNote] = useState('');
  const [busyUid, setBusyUid] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setAccounts(await listDjAccounts());
    } catch (error) {
      console.error('List DJ accounts failed', error);
      setLoadError('Could not load the DJ accounts. Try Refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const changePaused = async (account: DjAccount, paused: boolean, note?: string) => {
    setBusyUid(account.uid);
    try {
      const result = await setDjAccountPaused(account.uid, paused, note);
      if (result.emailStatus === 'failed') {
        toast.error("Done, but the email didn't send.");
      } else {
        toast.success(paused ? `Paused ${djAccountName(account)}` : `Resumed ${djAccountName(account)}`);
      }
      setConfirmingPauseUid(null);
      setPauseNote('');
      await loadAccounts();
    } catch (error) {
      console.error(paused ? 'Pause DJ account failed' : 'Resume DJ account failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyUid(null);
    }
  };

  const changeLinkEditing = async (account: DjAccount, revoked: boolean) => {
    const question = revoked
      ? `Pause link editing for ${djAccountName(account)}? They can still add links for new songs; changes to existing links come to you for review. They get an email.`
      : `Restore link editing for ${djAccountName(account)}? They get an email.`;
    if (!window.confirm(question)) return;
    setBusyUid(account.uid);
    try {
      const result = await setLinkEditingRevoked(account.uid, revoked);
      if (result.emailStatus === 'failed') {
        toast.error("Done, but the email didn't send.");
      } else {
        toast.success(revoked ? `Paused link editing for ${djAccountName(account)}` : `Restored link editing for ${djAccountName(account)}`);
      }
      await loadAccounts();
    } catch (error) {
      console.error(revoked ? 'Pause link editing failed' : 'Restore link editing failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyUid(null);
    }
  };

  const handleResume = (account: DjAccount) => {
    if (!window.confirm(`Resume ${djAccountName(account)}? They can sign in again right away.`)) return;
    changePaused(account, false);
  };

  const openPauseConfirm = (account: DjAccount) => {
    setConfirmingPauseUid(account.uid);
    setPauseNote('');
  };

  return (
    <section className={sectionClass}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={headingClass}>DJ accounts</h2>
        <button type="button" className="btn btn-secondary" onClick={loadAccounts} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Live = during the event&apos;s scheduled time (your time zone) or a song played in the last 2 hours.
      </p>

      {loadError && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {loadError}
        </div>
      )}

      {loading && accounts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading DJ accounts…</p>
      ) : !loadError && accounts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No DJ accounts yet.</p>
      ) : (
        <ul className="space-y-3">
          {accounts.map((account) => (
            <li key={account.uid} data-testid="dj-account-row" className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1 min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{account.displayName || '(no name)'}</span>
                    {account.isBeta && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">Beta</span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${account.paused
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                        : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'}`}
                    >
                      {account.paused ? `Paused since ${formatDate(account.pausedAt)}` : 'Active'}
                    </span>
                    {account.liveEvent && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                        Live now: {account.liveEvent.eventName}
                      </span>
                    )}
                    {account.linkEditingRevoked && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                        Link editing paused
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 break-all">{account.email}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Last sign-in: {formatDate(account.lastSignInAt)}</p>
                  {account.paused && account.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">{account.note}</p>
                  )}
                </div>
                {account.isOwner ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Owner</span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {account.paused ? (
                      <button type="button" className="btn btn-secondary" disabled={busyUid === account.uid} onClick={() => handleResume(account)}>
                        Resume
                      </button>
                    ) : confirmingPauseUid !== account.uid ? (
                      <button type="button" className="btn btn-secondary" disabled={busyUid === account.uid} onClick={() => openPauseConfirm(account)}>
                        Pause
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busyUid === account.uid}
                      onClick={() => changeLinkEditing(account, !account.linkEditingRevoked)}
                    >
                      {account.linkEditingRevoked ? 'Restore link editing' : 'Pause link editing'}
                    </button>
                  </div>
                )}
              </div>

              {!account.isOwner && !account.paused && confirmingPauseUid === account.uid && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-2">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Pause {djAccountName(account)}? They are signed out right away and can&apos;t sign in until you resume them.
                    Their event pages stay open, and new songs wait as pending.
                  </p>
                  <label htmlFor={`pause-note-${account.uid}`} className="block text-sm text-gray-700 dark:text-gray-300">
                    Private note (optional, only you see it)
                  </label>
                  <textarea
                    id={`pause-note-${account.uid}`}
                    value={pauseNote}
                    maxLength={MAX_PAUSE_NOTE_LENGTH}
                    onChange={(event) => setPauseNote(event.target.value)}
                    className="input w-full"
                    rows={2}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busyUid === account.uid}
                      onClick={() => changePaused(account, true, pauseNote)}
                    >
                      {busyUid === account.uid ? 'Pausing…' : 'Confirm pause'}
                    </button>
                    <button type="button" className="btn btn-secondary" disabled={busyUid === account.uid} onClick={() => setConfirmingPauseUid(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function isUpgradePending(event: Event): boolean {
  return event.sizeGate?.gateEnabled === true && event.sizeGate.pendingTierCap !== null;
}

function isPastEvent(event: Event): boolean {
  if (event.status === 'completed') return true;
  return Boolean(event.date && event.startTime) && computeEventStatus(event) === 'completed';
}

function sortGatedEvents(events: Event[]): Event[] {
  return [...events].sort(
    (first, second) =>
      Number(isUpgradePending(second)) - Number(isUpgradePending(first)) || (second.date ?? '').localeCompare(first.date ?? '')
  );
}

interface SizeGateBannerProps {
  events: Event[];
  busyEventId: string | null;
  onApprove: (event: Event) => void;
}

function SizeGateBanner({ events, busyEventId, onApprove }: SizeGateBannerProps) {
  const pending = events.filter(isUpgradePending);
  if (pending.length === 0) return null;
  return (
    <div className="space-y-2">
      {pending.map((event) => (
        <div
          key={event.id}
          role="alert"
          data-testid="size-gate-banner"
          className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border-2 border-red-500 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="flex items-center gap-2 text-red-800 dark:text-red-200 font-medium">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            {event.name}: {event.guestCount ?? 0} guests, past {event.sizeGate?.tierCap}.
          </p>
          <button type="button" className="btn btn-primary" disabled={busyEventId === event.id} onClick={() => onApprove(event)}>
            Approve {event.sizeGate?.pendingTierCap}
          </button>
        </div>
      ))}
    </div>
  );
}

interface BetaEventRowProps {
  event: Event;
  highlighted: boolean;
  busy: boolean;
  onToggleGate: (event: Event) => void;
  onChangeCap: (event: Event, tierCap: number) => void;
}

function BetaEventRow({ event, highlighted, busy, onToggleGate, onChangeCap }: BetaEventRowProps) {
  const sizeGate = event.sizeGate;
  if (!sizeGate) return null;
  return (
    <li
      id={`beta-event-${event.id}`}
      data-testid="beta-event-row"
      data-highlighted={highlighted ? 'true' : undefined}
      className={`p-4 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2 ${highlighted ? 'ring-2 ring-primary' : ''}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white">{event.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
            {event.date} · DJ {event.djId}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {event.guestCount ?? 0} / {sizeGate.tierCap} guests
            {isUpgradePending(event) && ` · Upgrade to ${sizeGate.pendingTierCap} pending`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            aria-pressed={sizeGate.gateEnabled}
            disabled={busy}
            onClick={() => onToggleGate(event)}
          >
            {sizeGate.gateEnabled ? 'Gate on' : 'Gate off'}
          </button>
          <label htmlFor={`tier-cap-${event.id}`} className="sr-only">Guest cap for {event.name}</label>
          <select
            id={`tier-cap-${event.id}`}
            className="input"
            value={sizeGate.tierCap}
            disabled={busy}
            onChange={(changeEvent) => onChangeCap(event, Number(changeEvent.target.value))}
          >
            {GUEST_TIERS.map((tier) => (
              <option key={tier} value={tier}>{tierLabel(tier)}</option>
            ))}
          </select>
        </div>
      </div>
    </li>
  );
}

interface BetaEventsSectionProps {
  events: Event[];
  highlightedEventId: string | null;
  busyEventId: string | null;
  onToggleGate: (event: Event) => void;
  onChangeCap: (event: Event, tierCap: number) => void;
}

function BetaEventsSection({ events, highlightedEventId, busyEventId, onToggleGate, onChangeCap }: BetaEventsSectionProps) {
  const scrolledRef = useRef(false);
  const currentEvents = events.filter((event) => !isPastEvent(event));
  const pastEvents = events.filter(isPastEvent);
  const highlightIsPast = pastEvents.some((event) => event.id === highlightedEventId);

  useEffect(() => {
    if (!highlightedEventId || scrolledRef.current) return;
    const row = document.getElementById(`beta-event-${highlightedEventId}`);
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    scrolledRef.current = true;
  }, [highlightedEventId, events]);

  const renderRow = (event: Event) => (
    <BetaEventRow
      key={event.id}
      event={event}
      highlighted={event.id === highlightedEventId}
      busy={busyEventId === event.id}
      onToggleGate={onToggleGate}
      onChangeCap={onChangeCap}
    />
  );

  return (
    <section className={sectionClass}>
      <h2 className={headingClass}>Beta events</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Guests are never turned away. With the gate on, you approve each tier. With it off, the event moves up by itself.
      </p>
      {currentEvents.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No current beta events.</p>
      ) : (
        <ul className="space-y-3">{currentEvents.map(renderRow)}</ul>
      )}
      {pastEvents.length > 0 && (
        <details open={highlightIsPast}>
          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400">
            Past events ({pastEvents.length})
          </summary>
          <ul className="mt-2 space-y-3">{pastEvents.map(renderRow)}</ul>
        </details>
      )}
    </section>
  );
}

export function AdminPage() {
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [requests, setRequests] = useState<InviteCodeRequest[]>([]);
  const [loadError, setLoadError] = useState('');
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);
  const [gatedEvents, setGatedEvents] = useState<Event[]>([]);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const highlightedEventId = searchParams.get('event');

  useEffect(() => {
    const handleError = (error: Error) => {
      console.error('Admin subscription failed', error);
      setLoadError('Could not load the admin data. Check that this account has the owner admin claim.');
    };
    const unsubscribeCodes = subscribeToInviteCodes(setCodes, handleError);
    const unsubscribeRequests = subscribeToInviteCodeRequests(setRequests, handleError);
    const unsubscribeGatedEvents = subscribeToGatedEvents((events) => setGatedEvents(sortGatedEvents(events)), handleError);
    return () => {
      unsubscribeCodes();
      unsubscribeRequests();
      unsubscribeGatedEvents();
    };
  }, []);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      const result = await createInviteCode(label.trim() || undefined);
      setNewCode(result.code);
      setLabel('');
      toast.success('Invite code created.');
    } catch (error) {
      console.error('Create invite code failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (code: InviteCode) => {
    if (!window.confirm(`Revoke ${formatInviteCode(code.code)}? It will stop working immediately.`)) return;
    try {
      await revokeInviteCode(code.id);
      toast.success('Code revoked.');
    } catch (error) {
      console.error('Revoke invite code failed', error);
      toast.error(signUpErrorMessage(error));
    }
  };

  const handleMarkHandled = async (request: InviteCodeRequest) => {
    try {
      await markInviteCodeRequestHandled(request.id);
      toast.success('Request marked handled.');
    } catch (error) {
      console.error('Mark request handled failed', error);
      toast.error(signUpErrorMessage(error));
    }
  };

  const handleSendCode = async (request: InviteCodeRequest) => {
    if (!window.confirm(`Send an invite code to ${request.email}?`)) return;
    setBusyRequestId(request.id);
    try {
      const result = await sendInviteCodeForRequest(request.id);
      if (result.emailStatus === 'failed') {
        toast.error('Code created, but the email failed. Copy the sign-up link below and send it yourself.');
      } else if (result.emailStatus === 'skipped') {
        toast.error('Code created, but no email was sent. Copy the sign-up link below and send it yourself.');
      } else {
        toast.success(`Invite sent to ${request.email}`);
      }
    } catch (error) {
      console.error('Send invite code for request failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleDecline = async (request: InviteCodeRequest) => {
    if (!window.confirm(`Send ${request.name} the "not right now" email?`)) return;
    setBusyRequestId(request.id);
    try {
      const result = await declineInviteCodeRequest(request.id);
      if (result.emailStatus === 'failed') {
        toast.error('Request declined, but the email failed.');
      } else if (result.emailStatus === 'skipped') {
        toast.error('Declined, but no email was sent.');
      } else {
        toast.success(`Declined ${request.name}`);
      }
    } catch (error) {
      console.error('Decline invite code request failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleApproveTier = async (event: Event) => {
    setBusyEventId(event.id);
    try {
      const result = await approveTierUpgrade(event.id);
      toast.success(`${event.name} is now at the ${tierLabel(result.tierCap)}.`);
    } catch (error) {
      console.error('Approve tier upgrade failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyEventId(null);
    }
  };

  const changeSizeGate = async (event: Event, update: { gateEnabled?: boolean; tierCap?: number }, successMessage: string) => {
    setBusyEventId(event.id);
    try {
      await updateEventSizeGate(event.id, update);
      toast.success(successMessage);
    } catch (error) {
      console.error('Update event size gate failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyEventId(null);
    }
  };

  const handleToggleGate = (event: Event) => {
    const gateEnabled = !event.sizeGate?.gateEnabled;
    const pendingTierCap = event.sizeGate?.pendingTierCap ?? null;
    const message = !gateEnabled && pendingTierCap !== null
      ? `Gate off for ${event.name}. The ${tierLabel(pendingTierCap)} was applied.`
      : `Gate ${gateEnabled ? 'on' : 'off'} for ${event.name}.`;
    changeSizeGate(event, { gateEnabled }, message);
  };

  const handleChangeCap = (event: Event, tierCap: number) => {
    const guestCount = event.guestCount ?? 0;
    if (tierCap < guestCount && !window.confirm(`${guestCount} guests are already in. Set the cap below that anyway?`)) return;
    changeSizeGate(event, { tierCap }, `${event.name} is now at the ${tierLabel(tierCap)}.`);
  };

  const activeCodes = codes.filter((code) => code.status === 'active');
  const closedCodes = codes.filter((code) => code.status !== 'active');
  const betaAccounts = codes.filter((code) => code.status === 'used');
  const pendingRequests = requests.filter((request) => request.status === 'pending');
  const handledRequests = requests.filter((request) => request.status === 'handled');
  const declinedRequests = requests.filter((request) => request.status === 'declined');
  const codesById = new Map(codes.map((code) => [code.id, code]));

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Speaker className="w-6 h-6 text-primary" />
            <span className="text-lg font-bold text-gray-900 dark:text-white">VoteBeats Admin</span>
          </div>
          <Link to="/dashboard" className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover">
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        <SizeGateBanner events={gatedEvents} busyEventId={busyEventId} onApprove={handleApproveTier} />

        {loadError && (
          <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {loadError}
          </div>
        )}

        <section className={sectionClass}>
          <h2 className={headingClass}>Invite codes</h2>
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
            <label htmlFor="invite-label" className="sr-only">Who is this code for?</label>
            <input
              id="invite-label"
              type="text"
              value={label}
              maxLength={100}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Who is this code for? (optional)"
              className="input flex-1"
            />
            <button type="submit" className="btn btn-primary" disabled={creating}>
              <Plus className="w-5 h-5 mr-2" />
              {creating ? 'Creating…' : 'Create code'}
            </button>
          </form>

          {newCode && (
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 space-y-2">
              <p className="text-sm text-gray-700 dark:text-gray-300">New code:</p>
              <p className="text-2xl font-mono font-bold text-gray-900 dark:text-white" data-testid="new-invite-code">
                {formatInviteCode(newCode)}
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard(formatInviteCode(newCode), 'Code copied.')}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy code
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard(signUpLink(newCode), 'Sign-up link copied.')}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy sign-up link
                </button>
              </div>
            </div>
          )}

          <h3 className="font-medium text-gray-900 dark:text-white">Active</h3>
          {activeCodes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No active codes.</p>
          ) : (
            <table className={tableClass}>
              <thead>
                <tr><th className={cellClass}>Code</th><th className={cellClass}>For</th><th className={cellClass}>Created</th><th className={cellClass} /></tr>
              </thead>
              <tbody>
                {activeCodes.map((code) => (
                  <tr key={code.id}>
                    <td className={`${cellClass} font-mono`}>{formatInviteCode(code.code)}</td>
                    <td className={cellClass}>{code.label ?? '—'}</td>
                    <td className={cellClass}>{formatDate(code.createdAt)}</td>
                    <td className={cellClass}>
                      <button type="button" className="text-red-600 hover:text-red-700 font-medium" onClick={() => handleRevoke(code)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 className="font-medium text-gray-900 dark:text-white">Used and revoked</h3>
          {closedCodes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">None yet.</p>
          ) : (
            <table className={tableClass}>
              <thead>
                <tr><th className={cellClass}>Code</th><th className={cellClass}>For</th><th className={cellClass}>Status</th><th className={cellClass}>Date</th></tr>
              </thead>
              <tbody>
                {closedCodes.map((code) => (
                  <tr key={code.id}>
                    <td className={`${cellClass} font-mono`}>{formatInviteCode(code.code)}</td>
                    <td className={cellClass}>{code.label ?? '—'}</td>
                    <td className={cellClass}>{code.status === 'used' ? 'Used' : 'Revoked'}</td>
                    <td className={cellClass}>{formatDate(code.status === 'used' ? code.usedAt : code.revokedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className={sectionClass}>
          <h2 className={headingClass}>Code requests</h2>
          {pendingRequests.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No pending requests.</p>
          ) : (
            <ul className="space-y-3">
              {pendingRequests.map((request) => (
                <li key={request.id} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{request.name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(request.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{request.email}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{request.note}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busyRequestId === request.id}
                      onClick={() => handleSendCode(request)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send code
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busyRequestId === request.id}
                      onClick={() => handleDecline(request)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Decline
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busyRequestId === request.id}
                      onClick={() => handleMarkHandled(request)}
                    >
                      Mark handled
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {handledRequests.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400">
                Handled requests ({handledRequests.length})
              </summary>
              <ul className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 text-sm text-gray-600 dark:text-gray-400">
                {handledRequests.map((request) => {
                  const sentCode = request.sentCodeId ? codesById.get(request.sentCodeId) : undefined;
                  return (
                    <li key={request.id} className="px-3 py-3 space-y-1">
                      <p>
                        <span className="font-medium text-gray-900 dark:text-white">{request.name}</span> ({request.email}), handled {formatDate(request.handledAt)}
                      </p>
                      {sentCode && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono">{formatInviteCode(sentCode.code)}</span>
                          <button
                            type="button"
                            className="text-primary hover:text-primary-hover font-medium"
                            onClick={() => copyToClipboard(signUpLink(sentCode.code), 'Sign-up link copied.')}
                          >
                            Copy sign-up link
                          </button>
                        </div>
                      )}
                      <EmailProblem request={request} />
                    </li>
                  );
                })}
              </ul>
            </details>
          )}

          <h3 className="font-medium text-gray-900 dark:text-white">Declined — waiting for launch</h3>
          {declinedRequests.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No declined requests.</p>
          ) : (
            <ul className="space-y-2">
              {declinedRequests.map((request) => (
                <li key={request.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium text-gray-900 dark:text-white">{request.name}</span> ({request.email}), declined {formatDate(request.declinedAt)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busyRequestId === request.id}
                      onClick={() => handleSendCode(request)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send code
                    </button>
                  </div>
                  <EmailProblem request={request} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <NextEventContactsSection />

        <BetaEventsSection
          events={gatedEvents}
          highlightedEventId={highlightedEventId}
          busyEventId={busyEventId}
          onToggleGate={handleToggleGate}
          onChangeCap={handleChangeCap}
        />

        <section className={sectionClass}>
          <h2 className={headingClass}>Beta accounts</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Accounts awaiting verification can be removed in the Firebase Console.
          </p>
          {betaAccounts.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No beta accounts yet.</p>
          ) : (
            <table className={tableClass}>
              <thead>
                <tr><th className={cellClass}>Name</th><th className={cellClass}>Email</th><th className={cellClass}>Code</th><th className={cellClass}>Signed up</th><th className={cellClass}>Status</th></tr>
              </thead>
              <tbody>
                {betaAccounts.map((code) => (
                  <tr key={code.id}>
                    <td className={cellClass}>{code.usedByName ?? '—'}</td>
                    <td className={cellClass}>{code.usedByEmail ?? '—'}</td>
                    <td className={cellClass}>
                      <span className="font-mono">{formatInviteCode(code.code)}</span>
                      {code.label && <span className="text-gray-500 dark:text-gray-400"> ({code.label})</span>}
                    </td>
                    <td className={cellClass}>{formatDate(code.usedAt)}</td>
                    <td className={cellClass}>{code.activatedAt ? 'Active' : 'Awaiting verification'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <DjAccountsSection />
        <LinkLibrarySection />
        <DeletedEventsSection />
      </main>
    </div>
  );
}
